import DataTable, { Column } from "../../components/DataTable";
import PaginationBar from "../../components/PaginationBar";
import Tag from "../../components/Tags";
import { PageHeader } from "../../components";
import SubHeadings from "../../components/SubHeadings";
import { downloadIcon, info } from "../../asset";
import { useLocation, useNavigate } from "react-router-dom";
import CustomSelect from "../../components/CustomSelect";
import { AddStaffForm } from "../../types/staff";
import { formatRoleForDisplay } from "../../util/functions/UniversalFunctions";
import { useBranchCode } from "../../util/hooks/useBranch";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { useEffect } from "react";
import { fetchStaff, setFilters } from "../../features/staff/staffSlice";
import { useDispatch } from "react-redux";

export default function PayrollManagement() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { staffList, loading, filters, total } = useSelector(
    (state: RootState) => state.staff,
  );

  const branchCode = useBranchCode();

  useEffect(() => {
    if (!branchCode) return;

    dispatch(
      fetchStaff({
        ...filters,
        branchCode,
        page: 0,
        size: 40,
      }),
    );
  }, [dispatch, filters, branchCode]);

  const activeStaff = staffList.filter((staff) => staff.active);

  const columns: Column<AddStaffForm>[] = [
    {
      label: <input type="checkbox" className="w-3 h-3" />,
      accessor: "select",
      render: (item) => (
        <div className="relative h-full">
          <div
            className="absolute inset-y-0 left-0 right-[-1000px] cursor-pointer"
            onClick={() => {
              if (item.isFinanceVerified) {
                // navigate(`/payroll/staff-overview/${item.id}`);
                navigate(`/payroll-management/add-payroll/${item.staffId}`);
              } else {
                navigate(`/staff/staff-profile/${item.staffId}`, {
                  state: { showReminder: true },
                });
              }
            }}
          />

          <input
            type="checkbox"
            className="relative z-10 w-3 h-3"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ),
    },

    {
      label: "SL. NO.",
      accessor: "id",
      render: (item) => <span className="text-sm">{item.employeeCode}</span>,
    },
    {
      label: "Profile",
      accessor: "staffName",
      render: (item) => (
        <div>
          {!item?.profile_image_url ? (
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white font-semibold">
              {item.fullName.charAt(0).toUpperCase()}
            </div>
          ) : (
            <img
              src={item?.profile_image_url}
              alt="profile"
              className="w-10 h-10 rounded-full object-cover bg-background-200"
            />
          )}
        </div>
      ),
    },
    {
      label: "Staff Name",
      accessor: "fullName",
      searchable: true,
      render: (item) => (
        <div className="flex flex-col">
          <span className="font-bold text-sm text-neutral-400">
            {item.fullName}
          </span>
          <span className="text-xs text-neutral-300">
            {formatRoleForDisplay(item.roleName)}
          </span>
        </div>
      ),
    },
    {
      label: "Staff Contact",
      accessor: "email",
      render: (item) => (
        <div className="relative">
          {item.isFinanceVerified ? (
            <div className="text-[13px] text-neutral-400">
              <div>{item.email}</div>
              <div>{item.mobile}</div>
            </div>
          ) : (
            <button
              onClick={() =>
                navigate(`/staff/staff-profile/${item.staffId}`, {
                  state: { showReminder: true },
                })
              }
              className="absolute left-0  top-2 -translate-y-1/2 z-10 border border-neutral-200 rounded bg-white"
            >
              <div className="flex items-center whitespace-nowrap gap-2 text-neutral-400 text-[13px] px-6 py-[6px]">
                <img src={info} alt="" />
                This employee’s profile is incomplete.
                <span className="text-primary-600 cursor-pointer underline">
                  Complete now to proceed with payroll.
                </span>
              </div>
            </button>
          )}
        </div>
      ),
    },
    {
      label: "Latest Payslip",
      accessor: "latestPayslipAvailable",
      render: (item) =>
        item.isFinanceVerified ? (
          <button className="flex gap-2 bg-success-50 px-[6px] py-[4.5px] rounded text-success-600 text-[11px] font-medium">
            <img src={downloadIcon} alt="" />
            Download
          </button>
        ) : null,
    },
    {
      label: "Action",
      accessor: "action",
      render: (item) =>
        item.isFinanceVerified ? (
          <Tag text="Manage Payroll" color="warning" size="sm" padding="wide" />
        ) : null,
    },
  ];

  const handlePageChange = (newPage: number) => {
    dispatch(setFilters({ page: newPage }));
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch(setFilters({ size: newSize, page: 0 }));
  };

  const handleSearchChange = (accessor: string, value: string) => {
    dispatch(setFilters({ [accessor]: value || "", page: 0 }));
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-between items-center">
        <PageHeader />
        {/* <div className="max-w-80">
          <CustomSelect
            name=""
            placeholder="Filter by Designation...."
            options={["Center Head", "Teacher"]}
            onChange={() => {}}
            value={""}
            selectClassName="w-fit border-none"
          />
        </div> */}
      </div>
      <PaginationBar
        currentPage={filters.page}
        pageSize={filters.size}
        totalItems={total}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <div className="bg-white px-6 py-5 flex flex-col gap-6 rounded-lg">
        <SubHeadings text="Active Employees" />
        <DataTable
          columns={columns}
          data={activeStaff}
          onSearchChange={handleSearchChange}
          isLoading={loading}
        />
      </div>
    </div>
  );
}
